import { useCallback, useMemo } from "react"

import type { ExtractedLink } from "../thread-context"
import { useThreadContext } from "../thread-context"
import { createDebouncedLinkExtractor } from "../utils/link-extractor"

/**
 * Hook for managing editor link extraction and state
 */
export function useEditorLinks() {
	const { extractedLinks, setExtractedLinks } = useThreadContext()

	// Create a memoized debounced extractor
	const debouncedExtractor = useMemo(
		() => createDebouncedLinkExtractor(300),
		[],
	)

	/**
	 * Extract links from HTML content and update state
	 */
	const extractAndUpdateLinks = useCallback(
		(htmlContent: string) => {
			debouncedExtractor(htmlContent, (links: ExtractedLink[]) => {
				// Only update if links have actually changed
				const currentUrls = extractedLinks.map((link) => link.url).sort()
				const newUrls = links.map((link) => link.url).sort()

				const hasChanged =
					currentUrls.length !== newUrls.length ||
					currentUrls.some((url, index) => url !== newUrls[index])

				if (hasChanged) {
					setExtractedLinks(links)
				}
			})
		},
		[debouncedExtractor, extractedLinks, setExtractedLinks],
	)

	/**
	 * Clear all extracted links
	 */
	const clearExtractedLinks = useCallback(() => {
		setExtractedLinks([])
	}, [setExtractedLinks])

	/**
	 * Remove a specific link by ID
	 */
	const removeExtractedLink = useCallback(
		(linkId: string) => {
			setExtractedLinks(extractedLinks.filter((link) => link.id !== linkId))
		},
		[extractedLinks, setExtractedLinks],
	)

	return {
		extractedLinks,
		extractAndUpdateLinks,
		clearExtractedLinks,
		removeExtractedLink,
	}
}
